import { StepThreeData } from "@/app/(protected)/concepting/components/StepThree/types";
import { StepOneData, Guidelines } from "@/app/(protected)/concepting/components/StepOne/types";
import { StepTwoData, AssetIdData } from "@/app/(protected)/concepting/components/StepTwo/types";
import { ImageGenerationTypeData, StepTwoLifeStyle } from "@/app/(protected)/generate-lifestyle/components/StepTwo/types";
import {
  STAGE_NAMES,
  ASSET_GENERATION_TYPES,
  GTM_ALLOWED_PROJECT_ROLES,
  PROJECT_TYPES,
  URLS,
  DELIVERABLE_TYPE,
} from "@/constants/constants";
import { StepFourLanguageData, DBRaiOutputsCheck } from "@/app/(protected)/concepting/components/StepFour/types";
import { RAI_STATUS } from "@/types";
import { PdpCopyList, YesNo } from "@/app/(protected)/pdp-copy/components/ProjectCopyList/types";
import { AssetTypesDb } from "@/app/(protected)/saved-projects/types";
import { AssetType, ExploreOptions } from "@/app/(protected)/asset-generation/components/ExploreOptions/types";
import { StartCreating } from "@/app/(protected)/asset-generation/components/StartCreating/types";
import { AddInputs } from "@/app/(protected)/asset-generation/components/AddInputs/types";
import { ASSET_VARIANTS, AssetTypeEnum, LANGUAGE } from "@/app/(protected)/asset-generation/components/ReviewAssets/components/constants";
import { FULLY_GENERATED_IMAGE, IMAGE_GENERATED_BACKGROUND } from "@/components/ProductPlacementImage/constants";
import { RadioOption } from "@/components/CustomRadio";
import { PersistedReferenceImageState } from "@/app/(protected)/components/ReferenceImageFGAndPPMerge/types";
import { useVideoGenerationStore } from "@/app/(protected)/video-generation/store/useVideoGenerationStore";
import { useStepStore } from "@/app/store/useStepStore";
import { STEP_NUMBERS } from "@/app/context/ProjectProvider/constants";
import { lowerCaseConversion } from "./string.utils";
import { DEFAULT_NUMBER_OF_IMAGES } from "@/app/(protected)/asset-generation/components/ExploreOptions/constant";
import { Dispatch } from "react";
import { ProjectRole } from "@/app/(protected)/concepting/components/StepsWrapper";
import { Actions } from "@/app/context/AppProvider/reducer";
import { setCurrentPageName, setCurrentStepName, setCurrentUserflow } from "@/app/context/AppProvider/actions";

export interface StepsDataProps {
  stepOneData: {
    concepter: StepOneData;
    lifeStyle: StepOneData;
    pdpCopy: StepOneData;
    assetGeneration: ExploreOptions;
    backgroundGeneration: ExploreOptions & { isSavedProject: boolean; selectedAspectRatio: string };
  };
  stepTwoData: {
    concepter?: StepTwoData;
    lifeStyle: StepTwoLifeStyle;
    pdpCopy: PdpCopyList;
    assetGeneration: AddInputs;
    backgroundGeneration: AddInputs;
    assetId: AssetIdData;
  };
  stepThreeData: { concepter?: StepThreeData; assetGeneration: StartCreating; backgroundGeneration: StepFourLanguageData };
  stepFourData: { concepter?: StepFourLanguageData; assetGeneration: StepFourLanguageData; backgroundGeneration: StepFourLanguageData };
  projectId: string;
  projectOutputId: string;
  projectType: string;
  creativeKb?: string;
  audienceKb?: string;
  brandKb?: string;
  channelKb?: string;
  conceptId?: string;
  isSavedProject: boolean;
  currentStep: number;
  selectedAccordion?: number;
  stage: string;
  brandId: string;
  version?: number;
  savedWorkCache?: { rowId: string };
  selectedAspectRatio?: string;
  downloadStartTime?: number;
  genaratedImageTypeData?: ImageGenerationTypeData;
  selectedImageGenerationType?: RadioOption;
  skipRaiCheckOnce?: boolean;
  referenceImageOptions?: {
    referenceImage: { assetUrl: string; assetId: string }[];
  };
}

export const defaultStepData: StepOneData = {
  projectName: "",
  files: [
    {
      file: null,
      id: "",
    },
  ],
};

const defaultReferenceImagesState: PersistedReferenceImageState = {
  files: [],
  isDisclaimerChecked: false,
};

export const defaultRaiData = {
  data: {
    description: {
      status: RAI_STATUS.PASS,
      errorMessages: [],
    },
    image: {
      status: RAI_STATUS.PASS,
      errorMessages: [],
    },
    videoScript: {
      status: RAI_STATUS.PASS,
      errorMessages: [],
    },
  },
};

export const outputDefaultData: StepFourLanguageData = {
  outputId: "",
  language: LANGUAGE.ENGLISH,
  modelOutputs: {
    type: "",
    languages: {
      english: {
        description: {
          title: "",
          description: "",
        },
        image: {
          pngURL: "",
          jpegURL: "",
        },
        videoScript: {
          title: "",
          description: "",
          scenes: [],
        },
      },
    },
  },
  raiOutputsCheck: defaultRaiData,
  videoDuration: 0,
  gsUrls: {
    jpegURL: "",
    pngURL: "",
  },
};

export const stepTwoDataLifestyle: StepTwoLifeStyle = {
  outputId: "",
  uploadedExcelData: {},
  selectedGtins: [],
  selectedlifeStyle: "",
  generatedContent: {
    selectedBrand: "",
    imagePrompt: "",
    output: [
      {
        jpegURL: "",
        pngURL: "",
      },
    ],
  },
  xRequestIdRaiCheck: "",
  productImageOption: { id: IMAGE_GENERATED_BACKGROUND, label: "" },
  cachedSelectedProductImage: "",
  productPlacementBase64Data: "",
  productPlacementImageUrl: "",
  cachedAccordionData: {
    description: "",
    textOnImage: "",
    imageUrl: "",
    aspectRatio: "",
    conceptId: "",
  },
  cachedRaiResults: [],
};

export const stepTwoDataPdpCopy: PdpCopyList = {
  uploadedExcelData: {},
  selectedGtins: [],
  selectedPdpCopy: "",
  selectedBrand: "",
  outputId: "",
  generatedContent: {
    longDescription: "",
    shortDescription: "",
    longBullets: [],
    shortBullets: [],
    searchTerms: ["keyword1"],
    standardTitle: "",
    customerSpecificDescription: "",
    withoutBrandDescription: "",
  },
  generatedContentPayload: {
    gtin: "",
    briefAttributes: {
      gtin: "",
      productType: "",
      newRepeat: "",
      refreshYN: "N" as YesNo,
      programRationaleConsumerFacing: "",
      productNameAndQuantity: "",
      flavorTextureDescriptor: "",
      claimsIfApplicable: "",
      usageOccasion: "",
    },
  },
  cachedAccordionData: {
    description: "",
    keyword: "",
  },
  cachedRaiResults: [],
};

export const createStepTwoDataLifestyle = (): StepTwoLifeStyle => stepTwoDataLifestyle;

export const getDefaultStepsUtilData = (): StepsDataProps => {
  return {
    stepOneData: {
      concepter: { ...defaultStepData },
      lifeStyle: { ...defaultStepData },
      pdpCopy: { ...defaultStepData },
      assetGeneration: {
        projectName: "",
        copyOutput: { id: "", label: "" },
        channel: { id: "", label: "" },
        imageDimensions: "",
        specifications: { id: "", label: "" },
        videoScriptIncludeImage: false,
        scriptLength: { id: "", label: "" },
        assetType: "",
        numberOfImages: DEFAULT_NUMBER_OF_IMAGES,
        headlineText: "",
        productImageOption: { id: FULLY_GENERATED_IMAGE, label: "" },
        cachedSelectedProductImage: "",
        productPlacementBase64Data: "",
        productPlacementImageUrl: "",
        files: [
          {
            file: null,
            id: "",
          },
        ],
        areOptionsChanged: false,
        skipConceptCheck: false,
        referenceImagesState: defaultReferenceImagesState,
      },
      backgroundGeneration: {
        projectName: "",
        copyOutput: { id: "", label: "" },
        channel: { id: "", label: "" },
        imageDimensions: "",
        specifications: { id: "", label: "" },
        videoScriptIncludeImage: false,
        scriptLength: { id: "", label: "" },
        assetType: "",
        numberOfImages: DEFAULT_NUMBER_OF_IMAGES,
        headlineText: "",
        productImageOption: { id: FULLY_GENERATED_IMAGE, label: "" },
        cachedSelectedProductImage: "",
        productPlacementBase64Data: "",
        productPlacementImageUrl: "",
        isSavedProject: false,
        selectedAspectRatio: "",
        files: [
          {
            file: null,
            id: "",
          },
        ],
        areOptionsChanged: false,
        skipConceptCheck: false,
        referenceImagesState: defaultReferenceImagesState,
      },
    },
    stepTwoData: {
      concepter: { brandId: "", briefSummary: "", audienceInsights: [], isFirstSubmit: false, briefRAIStatus: [] },
      lifeStyle: stepTwoDataLifestyle,
      pdpCopy: stepTwoDataPdpCopy,
      assetGeneration: { generateType: "", selectedSavedProjectId: "", cachedSavedProjects: [] },
      backgroundGeneration: { generateType: "", selectedSavedProjectId: "", cachedSavedProjects: [] },
      assetId: { assetIdDescription: "" },
    },
    stepThreeData: {
      concepter: {
        content: [],
        raiResults: [],
        productImageOption: { id: FULLY_GENERATED_IMAGE, label: "" },
        cachedSelectedProductImage: "",
        productPlacementBase64Data: "",
      },
      assetGeneration: {
        textDescription: "",
        imageDescription: "",
      },
      backgroundGeneration: outputDefaultData,
    },
    stepFourData: {
      concepter: outputDefaultData,
      assetGeneration: outputDefaultData,
      backgroundGeneration: outputDefaultData,
    },
    projectId: "",
    projectType: "",
    creativeKb: "",
    audienceKb: "",
    brandKb: "",
    channelKb: "",
    isSavedProject: false,
    currentStep: 1,
    stage: "",
    brandId: "",
    conceptId: "",
    projectOutputId: "",
    savedWorkCache: {
      rowId: "",
    },
    selectedImageGenerationType: { id: "lifestyle", label: "Lifestyle" },
    genaratedImageTypeData: {
      lifestyle: createStepTwoDataLifestyle(),
      product: createStepTwoDataLifestyle(),
      claim: createStepTwoDataLifestyle(),
    },
  };
};

export const stepsUtilData: StepsDataProps = getDefaultStepsUtilData();

export const resetStepsData = () => {
  const defaultData = getDefaultStepsUtilData();
  const deepCopy = structuredClone(defaultData);
  Object.assign(stepsUtilData, deepCopy);
  useVideoGenerationStore.getState().resetAll();
  useStepStore.getState().setCurrentStep(STEP_NUMBERS.ONE);
};

const defaultBackgroundStepOneData: ExploreOptions = {
  projectName: "",
  copyOutput: { id: "", label: "" },
  channel: { id: "", label: "" },
  imageDimensions: "",
  specifications: { id: "", label: "" },
  videoScriptIncludeImage: false,
  scriptLength: { id: "", label: "" },
  assetType: "",
  numberOfImages: DEFAULT_NUMBER_OF_IMAGES,
  headlineText: "",
  productImageOption: { id: FULLY_GENERATED_IMAGE, label: "" },
  cachedSelectedProductImage: "",
  productPlacementBase64Data: "",
  productPlacementImageUrl: "",
  files: [{ file: null, id: "" }],
  areOptionsChanged: false,
  skipConceptCheck: false,
};

export function isForwardStage(newStage: string | null, oldStage: string | null): boolean {
  if (!oldStage || !newStage) return true;

  const stagesOrderMapper = {
    [STAGE_NAMES.ASSET_SELECTION]: 1,
    [STAGE_NAMES.VIDEO_PROJECT_CREATION]: 1,
    [STAGE_NAMES.BACKGROUND_PROJECT_OPTIONS]: 1,

    [STAGE_NAMES.ENTER_DESCRIPTION]: 2,
    [STAGE_NAMES.BRIEF_UPLOAD]: 2,
    [STAGE_NAMES.USE_A_CONCEPT]: 2,
    [STAGE_NAMES.VIDEO_PROJECT_INPUTS]: 2,

    [STAGE_NAMES.CAMPAIGN_DETAILS_REVIEW]: 3,
    [STAGE_NAMES.VIDEO_PROJECT_REVIEW]: 3,

    [STAGE_NAMES.CONCEPT_SELECTION]: 4,
    [STAGE_NAMES.PROJECT_IMAGE_LIST]: 4,
    [STAGE_NAMES.PROJECT_COPY_LIST]: 4,
  };

  // @ts-ignore newStage/oldStage shoud be valid stages and stagesOrderMapper should have the key, otherwise throw an error
  if (!stagesOrderMapper[oldStage]) throw new Error(`Project versioning check isForwardStage failed due to invalid oldStage: ${oldStage}`);
  // @ts-ignore newStage/oldStage shoud be valid stages and stagesOrderMapper should have the key, otherwise throw an error
  if (!stagesOrderMapper[newStage]) throw new Error(`Project versioning check isForwardStage failed due to invalid newStage: ${newStage}`);

  // @ts-ignore newStage/oldStage are checked to be valid stages => otherwise an error its thrown
  return stagesOrderMapper[newStage] > stagesOrderMapper[oldStage];
}

const getVisualTextType = (assetVariant?: ASSET_VARIANTS, modelKey?: string): string => {
  switch (assetVariant) {
    case ASSET_VARIANTS.HEADLINE_OVER_IMAGES_TEXT:
      return modelKey === "description" ? ASSET_VARIANTS.HEADLINES : "Visual with Text";
    case ASSET_VARIANTS.SHORT_DESCRIPTION:
      return modelKey === "description" ? ASSET_VARIANTS.SHORT_FORM : AssetTypeEnum.Visual;
    default:
      return AssetTypeEnum.Visual;
  }
};

export const normalizeAssetType = (
  assetType: AssetType | AssetTypesDb,
  assetVariant?: ASSET_VARIANTS,
  modelKey?: string,
  isTable?: boolean,
): string => {
  if (!stepsUtilData.isSavedProject && !isTable) return assetType;

  const assetMapping: Record<string, string> = {
    [ASSET_GENERATION_TYPES.VISUAL_TEXT]: getVisualTextType(assetVariant, modelKey ?? "") || AssetTypeEnum.VisualAndText,
    [ASSET_GENERATION_TYPES.TEXT]: assetVariant ?? AssetTypeEnum.Text,
    [ASSET_GENERATION_TYPES.VIDEO_SCRIPT]: AssetTypeEnum.VideoScript,
    [ASSET_GENERATION_TYPES.VISUAL]: AssetTypeEnum.Visual,
  };

  return assetMapping[assetType] || "";
};

export const mapRaiOutputsCheck = (dbResponse: DBRaiOutputsCheck) => {
  return {
    data: {
      description: dbResponse.description,
      image: dbResponse.image,
      images: dbResponse.images,
      videoScript: dbResponse.videoScript,
    },
  };
};

export const getDeliverableType = (deliverable?: string, defaultType = DELIVERABLE_TYPE.LIFESTYLE) => {
  const deliverableLower = lowerCaseConversion(deliverable);

  return [DELIVERABLE_TYPE.PRODUCT, DELIVERABLE_TYPE.CLAIM].includes(deliverableLower ?? "") ? deliverableLower : defaultType;
};

export const canRedirectToHome = (pathname: string) => {
  return [URLS.CONCEPTING, URLS.GENERATE_ASSETS, URLS.PDP_COPY, URLS.GENERATE_LIFESTYLE].some((url) => pathname.startsWith(url));
};

export const getStepsUtilDataWithFallback = (
  workflowUtils: StepsDataProps | null | undefined,
  stepsUtilData: StepsDataProps,
): {
  stepOneData: ExploreOptions;
  assetType: string;
  projectName: string;
  textLabel: string;
  projectId: string;
  projectType: string;
  selectedAspectRatio: string;
  version: number;
  stepFourData: StepFourLanguageData;
  selectedImageGenerationType?: RadioOption;
  downloadStartTime: number;
  brandId: string;
  brandKb: string;
  channelKb: string;
  projectOutputId: string;
} => {
  const isBackground = workflowUtils?.projectType === PROJECT_TYPES.BACKGROUND;

  // Choose data sources by workflow type
  const stepOneData: ExploreOptions = isBackground
    ? (workflowUtils?.stepOneData?.backgroundGeneration ?? defaultBackgroundStepOneData)
    : stepsUtilData.stepOneData.assetGeneration;

  const stepFourData: StepFourLanguageData = isBackground
    ? (workflowUtils?.stepThreeData?.backgroundGeneration ?? outputDefaultData)
    : (stepsUtilData.stepFourData.assetGeneration ?? outputDefaultData);

  // Destructure from the chosen base container
  const base = (isBackground ? workflowUtils : stepsUtilData) as StepsDataProps | undefined;
  const {
    projectId = "",
    projectType = "",
    selectedAspectRatio = "",
    version = 0,
    selectedImageGenerationType,
    downloadStartTime,
    brandId = "",
    brandKb = "",
    channelKb = "",
    projectOutputId = "",
  } = base ?? ({} as StepsDataProps);

  // Single, unified return object
  return {
    stepOneData,
    assetType: stepOneData.assetType ?? AssetTypeEnum.Visual,
    projectName: stepOneData.projectName ?? "",
    textLabel: stepOneData.copyOutput?.label ?? "",
    projectId,
    projectType,
    selectedAspectRatio,
    version,
    stepFourData,
    selectedImageGenerationType,
    downloadStartTime: downloadStartTime ?? Date.now(),
    brandId,
    brandKb,
    channelKb,
    projectOutputId,
  };
};

export function formatImageVersion(version: string): string {
  const formattedVersion = version.toString().replaceAll(".", "-");
  return `Img${formattedVersion}`;
}

export function setDefaultEventData(
  projectRole: ProjectRole,
  dispatch: Dispatch<Actions>,
  stepName: string,
  pageName: string,
  userflow: string,
) {
  if (GTM_ALLOWED_PROJECT_ROLES.includes(projectRole)) {
    dispatch(setCurrentStepName(stepName));
    dispatch(setCurrentPageName(pageName));
    dispatch(setCurrentUserflow(userflow));
  }
}

export const filterGuidelines = (guidelines: Guidelines[], excludedGuidelines: Guidelines[]) => {
  const filteredStrings = guidelines.filter((g) => !excludedGuidelines.includes(g));
  return [...filteredStrings];
};
